import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  ChevronLeft,
  Plus,
  Users,
  MessageSquare,
  Settings,
  Trash2,
  UserPlus,
  Edit,
  Mail,
} from "lucide-react";
import { toast } from "sonner";

const AdminSettings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("users");
  const [sendingReport, setSendingReport] = useState(false);
  const [users, setUsers] = useState([]);
  const [channels, setChannels] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create user modal
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    email: "",
    password: "",
    name: "",
    role: "staff",
    department: "",
    hotels: [],
  });
  const [createUserLoading, setCreateUserLoading] = useState(false);

  // Edit user modal
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserLoading, setEditUserLoading] = useState(false);
  const [availableHotels, setAvailableHotels] = useState([]);


  // Create channel modal
  const [createChannelOpen, setCreateChannelOpen] = useState(false);
  const [newChannel, setNewChannel] = useState({
    name: "",
    description: "",
    members: [],
  });
  const [createChannelLoading, setCreateChannelLoading] = useState(false);

  // Add members modal
  const [addMembersOpen, setAddMembersOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [addMembersLoading, setAddMembersLoading] = useState(false);

  // Check admin/management access
  useEffect(() => {
    if (user && !["super_admin", "admin", "management"].includes(user.role)) {
      toast.error("Accesso negato");
      navigate("/");
    }
  }, [user, navigate]);

  // Fetch users, channels and departments
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersRes, channelsRes, deptRes, hotelsRes] = await Promise.all([
          axios.get(`${API}/admin/users`),
          axios.get(`${API}/channels`),
          axios.get(`${API}/departments`),
          axios.get(`${API}/hotels`),
        ]);
        setUsers(usersRes.data);
        setChannels(channelsRes.data);
        setDepartments(deptRes.data);
        setAvailableHotels(hotelsRes.data);
      } catch (error) {
        toast.error("Errore nel caricamento dati");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Create user
  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.name) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }

    setCreateUserLoading(true);
    try {
      const response = await axios.post(`${API}/admin/users`, newUser);
      setUsers([...users, response.data]);
      setCreateUserOpen(false);
      setNewUser({ email: "", password: "", name: "", role: "staff", department: "", hotels: [] });
      toast.success("Utente creato con successo");
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore nella creazione");
    } finally {
      setCreateUserLoading(false);
    }
  };

  // Edit user
  const openEditUser = (u) => {
    setEditingUser({
      id: u.id,
      name: u.name,
      email: u.email,
      role: u.role,
      department: u.department || "",
      hotels: u.hotels || [],
      password: "",
    });
    setEditUserOpen(true);
  };

  const handleEditUser = async () => {
    if (!editingUser) return;

    setEditUserLoading(true);
    try {
      const updateData = {
        name: editingUser.name,
        role: editingUser.role,
        department: editingUser.department || null,
        hotels: editingUser.hotels || [],
      };
      if (editingUser.password) {
        updateData.password = editingUser.password;
      }
      
      const response = await axios.patch(`${API}/admin/users/${editingUser.id}`, updateData);
      setUsers(users.map((u) => (u.id === editingUser.id ? { ...u, ...response.data } : u)));
      setEditUserOpen(false);
      toast.success("Utente modificato con successo");
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore nella modifica");
    } finally {
      setEditUserLoading(false);
    }
  };

  // Delete user
  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo utente?")) return;

    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      setUsers(users.filter((u) => u.id !== userId));
      toast.success("Utente eliminato");
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Create channel
  const handleCreateChannel = async () => {
    if (!newChannel.name) {
      toast.error("Inserisci il nome del canale");
      return;
    }

    setCreateChannelLoading(true);
    try {
      const response = await axios.post(`${API}/channels`, {
        name: newChannel.name,
        description: newChannel.description,
        members: newChannel.members,
      });
      setChannels([...channels, { ...response.data, member_count: newChannel.members.length }]);
      setCreateChannelOpen(false);
      setNewChannel({ name: "", description: "", members: [] });
      toast.success("Canale creato con successo");
    } catch (error) {
      toast.error("Errore nella creazione del canale");
    } finally {
      setCreateChannelLoading(false);
    }
  };

  // Open add members modal
  const openAddMembers = (channel) => {
    setSelectedChannel(channel);
    setSelectedMembers([]);
    setAddMembersOpen(true);
  };

  // Add members to channel
  const handleAddMembers = async () => {
    if (selectedMembers.length === 0) {
      toast.error("Seleziona almeno un utente");
      return;
    }

    setAddMembersLoading(true);
    try {
      const response = await axios.patch(
        `${API}/channels/${selectedChannel.id}/members`,
        { member_ids: selectedMembers }
      );
      
      // Update channels list
      setChannels(
        channels.map((ch) =>
          ch.id === selectedChannel.id
            ? { ...ch, members: response.data.members, member_count: response.data.member_count }
            : ch
        )
      );
      
      setAddMembersOpen(false);
      toast.success("Membri aggiunti con successo");
    } catch (error) {
      toast.error("Errore nell'aggiunta dei membri");
    } finally {
      setAddMembersLoading(false);
    }
  };

  // Remove member from channel
  const handleRemoveMember = async (channelId, memberId) => {
    try {
      const response = await axios.delete(
        `${API}/channels/${channelId}/members/${memberId}`
      );
      
      setChannels(
        channels.map((ch) =>
          ch.id === channelId
            ? { ...ch, members: response.data.members, member_count: response.data.member_count }
            : ch
        )
      );
      
      toast.success("Membro rimosso");
    } catch (error) {
      toast.error("Errore nella rimozione");
    }
  };

  // Toggle member selection for new channel
  const toggleMemberForNewChannel = (userId) => {
    setNewChannel((prev) => ({
      ...prev,
      members: prev.members.includes(userId)
        ? prev.members.filter((id) => id !== userId)
        : [...prev.members, userId],
    }));
  };

  // Toggle member selection for adding to existing channel
  const toggleMemberSelection = (userId) => {
    setSelectedMembers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const getRoleBadge = (role) => {
    const styles = {
      super_admin: "bg-purple-100 text-purple-700",
      admin: "bg-red-100 text-red-700",
      management: "bg-blue-100 text-blue-700",
      staff: "bg-stone-100 text-stone-700",
    };
    const labels = {
      super_admin: "Super Admin",
      admin: "Admin",
      management: "Management",
      staff: "Staff",
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[role] || "bg-stone-100 text-stone-700"}`}>
        {labels[role] || role}
      </span>
    );
  };

  const getDepartmentName = (u) => {
    if (u.department_name) return u.department_name;
    if (u.department) {
      const dept = departments.find((d) => d.id === u.department);
      return dept ? dept.name : u.department;
    }
    return u.title || "";
  };

  const getUserName = (userId) => {
    const u = users.find((user) => user.id === userId);
    return u ? u.name : "Sconosciuto";
  };

  const canEditUser = (targetUser) => {
    if (user?.role === "admin" || user?.role === "super_admin") return true;
    if (user?.role === "management" && targetUser.role === "staff") return true;
    return false;
  };

  // Send daily report manually
  const handleSendReport = async () => {
    setSendingReport(true);
    try {
      const response = await axios.post(`${API}/admin/send-daily-report`);
      if (response.data.status === "partial") {
        toast.warning(response.data.message);
      } else {
        toast.success(`Report inviato a ${response.data.recipients.length} destinatari`);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore invio report");
    } finally {
      setSendingReport(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-pulse text-stone-400">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-stone-50/80 backdrop-blur-xl border-b border-stone-200">
        <div className="px-4 py-4 flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/")}
            className="text-stone-600 hover:text-stone-900"
            data-testid="back-button"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-outfit text-xl font-bold text-stone-900">
              Impostazioni Admin
            </h1>
            <p className="text-xs text-stone-500">Gestione utenti e canali</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-4 pb-2 flex gap-2 flex-wrap">
          <button
            onClick={() => setActiveTab("users")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              activeTab === "users"
                ? "bg-stone-900 text-white"
                : "bg-stone-100 text-stone-600 hover:bg-stone-200"
            }`}
            data-testid="tab-users"
          >
            <Users className="w-4 h-4 inline mr-2" />
            Utenti
          </button>
          <button
            onClick={() => setActiveTab("channels")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              activeTab === "channels"
                ? "bg-stone-900 text-white"
                : "bg-stone-100 text-stone-600 hover:bg-stone-200"
            }`}
            data-testid="tab-channels"
          >
            <MessageSquare className="w-4 h-4 inline mr-2" />
            Canali
          </button>
          {["super_admin", "admin"].includes(user?.role) && (
            <button
              onClick={() => setActiveTab("reports")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeTab === "reports"
                  ? "bg-stone-900 text-white"
                  : "bg-stone-100 text-stone-600 hover:bg-stone-200"
              }`}
              data-testid="tab-reports"
            >
              <Mail className="w-4 h-4 inline mr-2" />
              Report
            </button>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="p-4">
        {activeTab === "users" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-outfit text-lg font-semibold text-stone-900">
                Utenti ({users.length})
              </h2>
              <Button
                onClick={() => setCreateUserOpen(true)}
                className="rounded-full bg-stone-900 hover:bg-stone-800"
                data-testid="create-user-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuovo Utente
              </Button>
            </div>

            <div className="space-y-2" data-testid="users-list">
              {users.map((u) => (
                <div
                  key={u.id}
                  className="bg-white rounded-xl p-4 border border-stone-200"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-stone-200 flex items-center justify-center text-stone-600 font-semibold text-sm">
                        {u.name
                          .split(" ")
                          .map((n) => n[0])
                          .join("")
                          .toUpperCase()
                          .slice(0, 2)}
                      </div>
                      <div>
                        <p className="font-medium text-stone-900">{u.name}</p>
                        <p className="text-sm text-stone-500">{u.email}</p>
                        {u.hotels && u.hotels.length > 0 && (
                          <div className="flex gap-1 mt-0.5">
                            {u.hotels.map((h) => (
                              <span key={h} className="text-xs px-1.5 py-0.5 rounded bg-stone-100 text-stone-600">
                                {availableHotels.find((ah) => ah.slug === h)?.name || h}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getRoleBadge(u.role)}
                      {canEditUser(u) && u.id !== user?.id && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditUser(u)}
                            className="text-stone-500 hover:text-stone-700 hover:bg-stone-100"
                            data-testid={`edit-user-${u.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteUser(u.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-user-${u.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  {getDepartmentName(u) && (
                    <div className="mt-2 ml-13">
                      <span className="inline-flex items-center px-2 py-1 rounded-lg bg-amber-50 text-amber-700 text-xs font-medium">
                        {getDepartmentName(u)}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "channels" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-outfit text-lg font-semibold text-stone-900">
                Canali ({channels.length})
              </h2>
              <Button
                onClick={() => setCreateChannelOpen(true)}
                className="rounded-full bg-stone-900 hover:bg-stone-800"
                data-testid="create-channel-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuovo Canale
              </Button>
            </div>

            <div className="space-y-3" data-testid="channels-list">
              {channels.map((ch) => (
                <div
                  key={ch.id}
                  className="bg-white rounded-xl p-4 border border-stone-200"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-stone-900">{ch.name}</h3>
                      <p className="text-sm text-stone-500">{ch.description}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openAddMembers(ch)}
                      className="rounded-full"
                      data-testid={`add-members-${ch.id}`}
                    >
                      <UserPlus className="w-4 h-4 mr-1" />
                      Aggiungi
                    </Button>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {ch.members?.map((memberId) => (
                      <div
                        key={memberId}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-stone-100 rounded-full text-sm"
                      >
                        <span>{getUserName(memberId)}</span>
                        <button
                          onClick={() => handleRemoveMember(ch.id, memberId)}
                          className="w-4 h-4 rounded-full hover:bg-stone-300 flex items-center justify-center"
                          data-testid={`remove-member-${ch.id}-${memberId}`}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    {(!ch.members || ch.members.length === 0) && (
                      <span className="text-sm text-stone-400">Nessun membro</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "reports" && ["super_admin", "admin"].includes(user?.role) && (
          <div>
            <h2 className="font-outfit text-lg font-semibold text-stone-900 mb-4">
              Report Giornaliero
            </h2>

            <div className="bg-white rounded-xl p-6 border border-stone-200 space-y-4">
              <div>
                <h3 className="font-semibold text-stone-900 mb-2">Invio Automatico</h3>
                <p className="text-sm text-stone-600">
                  Il report giornaliero viene inviato automaticamente ogni giorno a mezzanotte 
                  a tutti gli utenti con ruolo <strong>Management</strong> e <strong>Admin</strong>.
                </p>
              </div>

              <div className="border-t border-stone-200 pt-4">
                <h3 className="font-semibold text-stone-900 mb-2">Invio Manuale</h3>
                <p className="text-sm text-stone-600 mb-4">
                  Puoi inviare immediatamente un report con i dati di oggi.
                </p>
                <Button
                  onClick={handleSendReport}
                  disabled={sendingReport}
                  className="rounded-full bg-amber-500 hover:bg-amber-600"
                  data-testid="send-report-button"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {sendingReport ? "Invio in corso..." : "Invia Report Ora"}
                </Button>
              </div>

              <div className="border-t border-stone-200 pt-4">
                <h3 className="font-semibold text-stone-900 mb-2">Contenuto del Report</h3>
                <ul className="text-sm text-stone-600 list-disc list-inside space-y-1">
                  <li>Tutti i messaggi inviati nella giornata per ogni canale</li>
                  <li>Compiti aperti (camera) con eventuali commenti</li>
                  <li>Compiti chiusi con nome utente e data di chiusura</li>
                  <li>Riepilogo totale messaggi, compiti aperti e chiusi</li>
                </ul>
              </div>

              <div className="border-t border-stone-200 pt-4">
                <h3 className="font-semibold text-stone-900 mb-2">Feedback Utenti</h3>
                <p className="text-sm text-stone-600 mb-4">
                  Scarica tutti i feedback anonimi raccolti dagli utenti.
                </p>
                <Button
                  onClick={async () => {
                    try {
                      const res = await axios.get(`${API}/admin/feedback/export`);
                      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = "feedback_endover.json";
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                      toast.success(`${res.data.length} feedback scaricati`);
                    } catch { toast.error("Errore nel download"); }
                  }}
                  variant="outline"
                  className="rounded-full"
                  data-testid="export-feedback-button"
                >
                  Scarica Feedback
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Create User Modal */}
      <Dialog open={createUserOpen} onOpenChange={setCreateUserOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit">Crea Nuovo Utente</DialogTitle>
            <DialogDescription>
              Inserisci i dati del nuovo utente
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                placeholder="Mario Rossi"
                data-testid="new-user-name"
              />
            </div>

            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="mario@endover.com"
                data-testid="new-user-email"
              />
            </div>

            <div className="space-y-2">
              <Label>Password *</Label>
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="••••••"
                data-testid="new-user-password"
              />
            </div>

            <div className="space-y-2">
              <Label>Ruolo *</Label>
              <Select
                value={newUser.role}
                onValueChange={(value) => setNewUser({ ...newUser, role: value })}
              >
                <SelectTrigger data-testid="new-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  {["super_admin", "admin"].includes(user?.role) && (
                    <>
                      <SelectItem value="management">Management</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Reparto</Label>
              <Select
                value={newUser.department}
                onValueChange={(value) => setNewUser({ ...newUser, department: value })}
              >
                <SelectTrigger data-testid="new-user-department">
                  <SelectValue placeholder="Seleziona reparto..." />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Hotels assignment */}
            <div>
              <Label>Hotel Assegnati</Label>
              <div className="flex flex-wrap gap-3 mt-2">
                {availableHotels.map((h) => (
                  <label key={h.slug} className="flex items-center gap-2 cursor-pointer">
                    <input
                      data-testid={`new-user-hotel-${h.slug}`}
                      type="checkbox"
                      checked={newUser.hotels.includes(h.slug)}
                      onChange={(e) => {
                        const hotels = e.target.checked
                          ? [...newUser.hotels, h.slug]
                          : newUser.hotels.filter((x) => x !== h.slug);
                        setNewUser({ ...newUser, hotels });
                      }}
                      className="rounded border-stone-300"
                    />
                    <span className="text-sm">{h.name}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-stone-400 mt-1">Admin e Management hanno accesso a entrambi automaticamente</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateUserOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={handleCreateUser}
              disabled={createUserLoading}
              className="bg-stone-900 hover:bg-stone-800"
              data-testid="confirm-create-user"
            >
              {createUserLoading ? "Creazione..." : "Crea Utente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={editUserOpen} onOpenChange={setEditUserOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit">Modifica Utente</DialogTitle>
            <DialogDescription>
              Modifica i dati di {editingUser?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input
                value={editingUser?.name || ""}
                onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                data-testid="edit-user-name"
              />
            </div>

            <div className="space-y-2">
              <Label>Nuova Password (lascia vuoto per non modificare)</Label>
              <Input
                type="password"
                value={editingUser?.password || ""}
                onChange={(e) => setEditingUser({ ...editingUser, password: e.target.value })}
                placeholder="••••••"
                data-testid="edit-user-password"
              />
            </div>

            {["super_admin", "admin"].includes(user?.role) && (
              <div className="space-y-2">
                <Label>Ruolo</Label>
                <Select
                  value={editingUser?.role || "staff"}
                  onValueChange={(value) => setEditingUser({ ...editingUser, role: value })}
                >
                  <SelectTrigger data-testid="edit-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="management">Management</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Reparto</Label>
              <Select
                value={editingUser?.department || "none"}
                onValueChange={(value) => setEditingUser({ ...editingUser, department: value === "none" ? "" : value })}
              >
                <SelectTrigger data-testid="edit-user-department">
                  <SelectValue placeholder="Seleziona reparto..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuno</SelectItem>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Hotels assignment */}
            <div className="space-y-2">
              <Label>Hotel Assegnati</Label>
              <div className="flex flex-wrap gap-3">
                {availableHotels.map((h) => (
                  <label key={h.slug} className="flex items-center gap-2 cursor-pointer">
                    <input
                      data-testid={`edit-user-hotel-${h.slug}`}
                      type="checkbox"
                      checked={(editingUser?.hotels || []).includes(h.slug)}
                      onChange={(e) => {
                        const hotels = e.target.checked
                          ? [...(editingUser?.hotels || []), h.slug]
                          : (editingUser?.hotels || []).filter((x) => x !== h.slug);
                        setEditingUser({ ...editingUser, hotels });
                      }}
                      className="rounded border-stone-300"
                    />
                    <span className="text-sm">{h.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUserOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={handleEditUser}
              disabled={editUserLoading}
              className="bg-stone-900 hover:bg-stone-800"
              data-testid="confirm-edit-user"
            >
              {editUserLoading ? "Salvataggio..." : "Salva Modifiche"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Channel Modal */}
      <Dialog open={createChannelOpen} onOpenChange={setCreateChannelOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit">Crea Nuovo Canale</DialogTitle>
            <DialogDescription>
              Crea un nuovo canale e seleziona i membri
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome Canale *</Label>
              <Input
                value={newChannel.name}
                onChange={(e) =>
                  setNewChannel({ ...newChannel, name: e.target.value })
                }
                placeholder="es: Manutenzione"
                data-testid="new-channel-name"
              />
            </div>

            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Textarea
                value={newChannel.description}
                onChange={(e) =>
                  setNewChannel({ ...newChannel, description: e.target.value })
                }
                placeholder="Descrizione del canale..."
                rows={2}
                data-testid="new-channel-description"
              />
            </div>

            <div className="space-y-2">
              <Label>Membri</Label>
              <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1">
                {users.map((u) => (
                  <label
                    key={u.id}
                    className="flex items-center gap-2 p-2 hover:bg-stone-50 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={newChannel.members.includes(u.id)}
                      onChange={() => toggleMemberForNewChannel(u.id)}
                      className="rounded"
                    />
                    <span className="text-sm">{u.name}</span>
                    <span className="text-xs text-stone-400">{getDepartmentName(u)}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-stone-500">
                Selezionati: {newChannel.members.length}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateChannelOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={handleCreateChannel}
              disabled={createChannelLoading}
              className="bg-stone-900 hover:bg-stone-800"
              data-testid="confirm-create-channel"
            >
              {createChannelLoading ? "Creazione..." : "Crea Canale"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Members Modal */}
      <Dialog open={addMembersOpen} onOpenChange={setAddMembersOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit">
              Aggiungi Membri a "{selectedChannel?.name}"
            </DialogTitle>
            <DialogDescription>
              Seleziona gli utenti da aggiungere al canale
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="max-h-60 overflow-y-auto border rounded-lg p-2 space-y-1">
              {users
                .filter((u) => !selectedChannel?.members?.includes(u.id))
                .map((u) => (
                  <label
                    key={u.id}
                    className="flex items-center gap-2 p-2 hover:bg-stone-50 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedMembers.includes(u.id)}
                      onChange={() => toggleMemberSelection(u.id)}
                      className="rounded"
                    />
                    <span className="text-sm font-medium">{u.name}</span>
                    {getDepartmentName(u) && (
                      <span className="text-xs text-stone-400">({getDepartmentName(u)})</span>
                    )}
                    {getRoleBadge(u.role)}
                  </label>
                ))}
              {users.filter((u) => !selectedChannel?.members?.includes(u.id))
                .length === 0 && (
                <p className="text-sm text-stone-400 text-center py-4">
                  Tutti gli utenti sono già membri
                </p>
              )}
            </div>
            <p className="text-xs text-stone-500 mt-2">
              Selezionati: {selectedMembers.length}
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddMembersOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={handleAddMembers}
              disabled={addMembersLoading || selectedMembers.length === 0}
              className="bg-stone-900 hover:bg-stone-800"
              data-testid="confirm-add-members"
            >
              {addMembersLoading ? "Aggiunta..." : "Aggiungi Membri"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSettings;
